import React, { useRef, useState, useEffect, useCallback } from 'react';
import propTypes from 'prop-types';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from 'lib/utils';
import TabButton from './TabButton';

function normalizeTabs(tabs) {
  return tabs.map(t =>
    typeof t === 'string' ? { id: t, label: t } : { id: t.id ?? t.label, ...t }
  );
}

export default function Tabs({
  tabs,
  activeTabId,
  onTabChange,
  scrollButtons,
  scrollAmount = 150,
  className,
  buttonClassName,
  trailing
}) {
  const normalizedTabs = normalizeTabs(tabs);
  const activeIndex = normalizedTabs.findIndex(t => t.id === activeTabId);

  const scrollRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const overflowing = el.scrollWidth > el.clientWidth;
    setHasOverflow(overflowing);
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    if (!scrollButtons) return;
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });

    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [scrollButtons, updateScrollState]);

  const handleTabClick = index => {
    onTabChange?.(normalizedTabs[index].id, index);
  };

  const scroll = dir => {
    scrollRef.current?.scrollBy({
      left: dir * scrollAmount,
      behavior: 'smooth'
    });
  };

  const chevronClass = disabled =>
    cn(
      'flex items-center justify-center w-6 h-6 rounded-full backdrop-blur-sm border border-white/20 transition-colors duration-200 shrink-0',
      disabled
        ? 'cursor-not-allowed bg-black/20 text-white/90'
        : 'cursor-pointer bg-black/30 hover:bg-black/50 text-white'
    );

  const showChevrons = scrollButtons && hasOverflow;

  return (
    <div className={cn('flex items-center gap-1 w-full')}>
      {showChevrons && (
        <button
          type='button'
          aria-label='Scroll tabs left'
          disabled={!canScrollLeft}
          onClick={() => scroll(-1)}
          className={chevronClass(!canScrollLeft)}
        >
          <ChevronLeft className='w-3.5 h-3.5' />
        </button>
      )}

      <div
        ref={scrollRef}
        className={cn(
          'flex gap-2 overflow-x-auto flex-nowrap w-full scrollbar-none',
          className
        )}
      >
        {normalizedTabs.map((tab, i) => (
          <div key={tab.id} className='shrink-0'>
            <TabButton
              tab={tab}
              isActive={activeIndex === i}
              onClick={() => handleTabClick(i)}
              className={buttonClassName}
            />
          </div>
        ))}
        {trailing ? <div className='shrink-0'>{trailing}</div> : null}
      </div>

      {showChevrons && (
        <button
          type='button'
          aria-label='Scroll tabs right'
          disabled={!canScrollRight}
          onClick={() => scroll(1)}
          className={chevronClass(!canScrollRight)}
        >
          <ChevronRight className='w-3.5 h-3.5' />
        </button>
      )}
    </div>
  );
}

Tabs.propTypes = {
  tabs: propTypes.arrayOf(
    propTypes.oneOfType([
      propTypes.string,
      propTypes.shape({
        id: propTypes.string,
        label: propTypes.string.isRequired,
        icon: propTypes.node,
        className: propTypes.string,
        activeClassName: propTypes.string
      })
    ])
  ).isRequired,
  activeTabId: propTypes.string.isRequired,
  onTabChange: propTypes.func.isRequired,
  scrollButtons: propTypes.bool,
  scrollAmount: propTypes.number,
  className: propTypes.string,
  buttonClassName: propTypes.string,
  trailing: propTypes.node
};
